import { Spin } from "antd";
import { useMemo } from "react";
import { Box } from "../../../../../components/box";
import { getDDMMYYYFormat, getHHmmFormat } from "../../../../../helper/dateTimeHelper";
import { ApprovalLevelItemInProgress } from "../../../../../services/vos/permission-lookup/approval-process.service";
import {
  ApprovalProcessApprover,
  ApprovalProcessHistory,
} from "../../../company-office/business-tickets-v2/components/approval-process";

interface ApprovalHistoryProps {
  data?: ApprovalLevelItemInProgress[];
  isLoading?: boolean;
  children?: React.ReactNode;
}

const ApprovalHistory = ({ data, isLoading = false, children }: ApprovalHistoryProps) => {
  const approvalSteps = useMemo<ApprovalProcessApprover[][]>(() => {
    const levels = data || [];
    if (!levels.length) return [];

    return levels.map((level) =>
      (level.Approvers || []).map((approver) => {
        const histories: ApprovalProcessHistory[] = [];

        // Tạo history từ RecDate và isApprove
        if (approver.RecDate && approver.isApprove) {
          let status: string | null = null;
          let label: string = "";

          // Map isApprove sang status cho history
          if (approver.isApprove === "NO") {
            status = "rejected";
            label = "Đã từ chối";
          } else if (approver.isApprove === "YES") {
            status = "approved";
            label = "Đã phê duyệt";
          }

          // Chỉ tạo history nếu có status hợp lệ
          if (status) {
            histories.push({
              label,
              status,
              updatedDateTime: approver.RecDate,
              reason: approver.Description || "",
            });
          }
        }

        return {
          title: level.Title,
          name: approver.FullName,
          email: approver.Email,
          isRequired: approver.DutyType === "MAIN",
          roles: approver.Name_Job_Title ? [approver.Name_Job_Title] : [],
          isApproved: approver.isApprove === "YES",
          isRejected: approver.isApprove === "NO",
          histories,
          isSub: approver.DutyType === "SUB",
        };
      }),
    );
  }, [data]);

  return (
    <Box title="Lịch sử phê duyệt" className="full-height box__detail-container">
      <div className="InputWraper">
        <div className="wraper">
          <Spin spinning={isLoading}>
            {(() => {
              try {
                const steps = approvalSteps;
                if (!steps || steps.length === 0) {
                  return null;
                }

                // Duyệt qua tất cả các steps và approvers để lấy histories
                const allHistories: Array<{
                  title: string;
                  name: string;
                  history: ApprovalProcessHistory;
                }> = [];

                steps.forEach((step) => {
                  step.forEach((approver) => {
                    if (approver.histories && approver.histories.length > 0) {
                      approver.histories.forEach((history) => {
                        allHistories.push({
                          title: approver.title,
                          name: approver.name,
                          history,
                        });
                      });
                    }
                  });
                });

                // Sắp xếp theo thời gian (mới nhất trước)
                allHistories.sort((a, b) => {
                  const dateA = new Date(a.history.updatedDateTime).getTime();
                  const dateB = new Date(b.history.updatedDateTime).getTime();
                  return dateB - dateA;
                });

                return allHistories.map((item, index) => {
                  const { title, name, history } = item;
                  const historyDate = new Date(history.updatedDateTime);
                  const formattedDate = `${getDDMMYYYFormat(historyDate)} ${getHHmmFormat(historyDate)}`;
                  const isRejected = history.status === "rejected";

                  return (
                    <div key={index} className="py-2 pl-3">
                      <p>
                        <span className="font-weight-bold">
                          {title}, {name}
                        </span>{" "}
                        {isRejected ? (
                          <>
                            đã từ chối với lý do: {history.reason} ({formattedDate})
                          </>
                        ) : (
                          <>
                            {history.label.toLowerCase()}, lưu ý: {history.reason} ({formattedDate})
                          </>
                        )}
                      </p>
                    </div>
                  );
                });
              } catch (error) {
                console.error("Error parsing approval steps:", error);
                return null;
              }
            })()}
          </Spin>
        </div>
      </div>

      {children}
    </Box>
  );
};

export default ApprovalHistory;
